import { z } from 'zod';
import { normalizarTelefone } from './crm';

// ─────────── Fase 7 · Inteligência (docs/10) ───────────

// ── Assistente ──

export const perguntaSchema = z.object({
  pergunta: z.string().trim().min(3, 'Escreva a pergunta').max(300),
});

export type FormatoValor = 'moeda' | 'numero' | 'dias' | 'percentual';
export interface GraficoAssistente {
  tipo: 'barra' | 'linha' | 'pizza' | 'numero';
  formato: FormatoValor;
  dados: { rotulo: string; valor: number }[];
}
export interface RespostaAssistente {
  /** Consulta do catálogo usada (null = não entendeu) */
  consulta: string | null;
  titulo: string;
  resposta: string;
  grafico: GraficoAssistente | null;
  /** Link para o detalhe no painel */
  rota: string | null;
  sugestoes: string[];
}

/** Perguntas de exemplo mostradas no assistente. */
export const PERGUNTAS_EXEMPLO = [
  'Quanto lucrei este mês?',
  'Lucro dos últimos 6 meses',
  'Quais carros estão parados há mais tempo?',
  'Quais modelos vendem mais rápido?',
  'Quanto dinheiro tenho parado no estoque?',
  'De onde vêm meus leads?',
  'Qual vendedor vendeu mais este mês?',
  'Onde estou gastando mais?',
] as const;

// ── Precificador ──

export const ALERTAS_PRECO = ['ok', 'caro', 'barato', 'sem_margem', 'sem_preco'] as const;
export type AlertaPreco = (typeof ALERTAS_PRECO)[number];
export const NOME_ALERTA_PRECO: Record<AlertaPreco, string> = {
  ok: 'Preço bom',
  caro: 'Caro demais para girar',
  barato: 'Barato demais',
  sem_margem: 'Mercado abaixo da margem desejada',
  sem_preco: 'Sem preço',
};

export interface SugestaoPreco {
  veiculoId: string;
  descricao: string;
  placa: string;
  diasParado: number;
  precoAtualCentavos: number | null;
  sugeridoCentavos: number;
  faixa: { minCentavos: number; maxCentavos: number };
  fipeCentavos: number | null;
  mercado: { quantidade: number; medianaCentavos: number | null; coletadoEm: string | null };
  /** Custo total × (1 + margem desejada) */
  pisoCentavos: number;
  custoTotalCentavos: number;
  margemNoSugeridoPercentual: number | null;
  /** Dias médios até vender este modelo na loja (vendas dos últimos 12 meses) */
  giroModeloDias: number | null;
  alerta: AlertaPreco;
  motivos: string[];
}

// ── Consultor ──

export const AREAS_RECOMENDACAO = [
  'estoque',
  'compra',
  'preco',
  'margem',
  'comercial',
  'financeiro',
] as const;
export type AreaRecomendacao = (typeof AREAS_RECOMENDACAO)[number];
export const NOME_AREA_RECOMENDACAO: Record<AreaRecomendacao, string> = {
  estoque: 'Capital travado',
  compra: 'O que comprar',
  preco: 'Preço',
  margem: 'Onde o lucro vaza',
  comercial: 'Comercial',
  financeiro: 'Financeiro',
};
export type Prioridade = 'alta' | 'media' | 'baixa';
export interface Recomendacao {
  id: string;
  area: AreaRecomendacao;
  prioridade: Prioridade;
  titulo: string;
  motivo: string;
  acao: string;
  /** Dinheiro em jogo (capital travado, lucro perdido etc.) */
  impactoCentavos: number | null;
  rota: string | null;
}

// ── Score de saúde ──

export type CorSaude = 'verde' | 'amarelo' | 'vermelho';
export interface FatorSaude {
  chave: string;
  titulo: string;
  nota: number;
  cor: CorSaude;
  motivo: string;
  acao: string;
  rota: string | null;
}
export interface ScoreSaude {
  nota: number;
  cor: CorSaude;
  /** Fator mais fraco (o que mais puxa a nota para baixo) */
  motivo: string;
  acao: string;
  rota: string | null;
  fatores: FatorSaude[];
}
export const corDaNota = (n: number): CorSaude =>
  n >= 70 ? 'verde' : n >= 45 ? 'amarelo' : 'vermelho';

// ── Pós-venda ──

export const TIPOS_POS_VENDA = ['revisao', 'seguro', 'ipva', 'troca'] as const;
export type TipoPosVenda = (typeof TIPOS_POS_VENDA)[number];
export const NOME_TIPO_POS_VENDA: Record<TipoPosVenda, string> = {
  revisao: 'Revisão',
  seguro: 'Renovação do seguro',
  ipva: 'IPVA',
  troca: 'Momento de troca',
};
export const STATUS_POS_VENDA = ['agendada', 'pendente', 'enviada', 'feita', 'cancelada'] as const;
export type StatusPosVenda = (typeof STATUS_POS_VENDA)[number];
export const NOME_STATUS_POS_VENDA: Record<StatusPosVenda, string> = {
  agendada: 'Agendada',
  pendente: 'Para fazer',
  enviada: 'Mensagem enviada',
  feita: 'Feita',
  cancelada: 'Cancelada',
};

export interface TarefaPosVenda {
  id: string;
  tipo: TipoPosVenda;
  status: StatusPosVenda;
  previstaPara: string;
  clienteId: string;
  cliente: string;
  telefone: string | null;
  consentimento: boolean;
  veiculo: string;
  vendaData: string;
  vendedorId: string | null;
  vendedor: string | null;
  mensagem: string;
  motivo: string | null;
  enviadaEm: string | null;
}

/**
 * Mensagens de pós-venda (modelos aprovados na Meta com estes nomes). O texto é o que aparece
 * na conversa; o envio fora da janela de 24 h usa o modelo.
 */
export const MODELO_POS_VENDA: Record<TipoPosVenda, string> = {
  revisao: 'pos_venda_revisao',
  seguro: 'pos_venda_seguro',
  ipva: 'pos_venda_ipva',
  troca: 'pos_venda_troca',
};
export function mensagemPosVenda(
  tipo: TipoPosVenda,
  d: { cliente: string; carro: string; loja: string },
): string {
  const nome = d.cliente.split(' ')[0];
  switch (tipo) {
    case 'revisao':
      return `Olá, ${nome}! Aqui é da ${d.loja}. Já está chegando a hora da revisão do seu ${d.carro}. Quer que a gente indique uma oficina de confiança ou agende para você?`;
    case 'seguro':
      return `Olá, ${nome}! O seguro do seu ${d.carro} completa um ano em breve. Quer que a ${d.loja} faça uma cotação de renovação sem compromisso?`;
    case 'ipva':
      return `Olá, ${nome}! Lembrete da ${d.loja}: o IPVA do seu ${d.carro} começa a vencer neste mês. Se precisar, ajudamos com o parcelamento.`;
    case 'troca':
      return `Olá, ${nome}! Faz um tempo que você está com o ${d.carro}. Que tal avaliar a troca por um modelo mais novo? A ${d.loja} avalia seu carro na hora.`;
  }
}

// ── Configuração ──

export const configInteligenciaSchema = z.object({
  /** Margem desejada sobre o custo total (compra + preparação), em % */
  margemDesejadaPercentual: z.number().min(0).max(60).default(15),
  /** Meta de lucro do mês (todas as filiais), em centavos */
  metaLucroMensalCentavos: z.number().int().nonnegative().nullable().default(null),
  resumoDiarioAtivo: z.boolean().default(false),
  /** WhatsApp do dono para o resumo diário */
  resumoTelefone: z
    .string()
    .trim()
    .nullable()
    .default(null)
    .transform((t) => (t ? normalizarTelefone(t) : null))
    .refine((t) => t === null || /^55\d{10,11}$/.test(t), 'WhatsApp inválido (DDD + número)'),
  /** Envia as mensagens de pós-venda sozinho (só para clientes com consentimento) */
  posVendaAutomatico: z.boolean().default(true),
  posVendaDiasRevisao: z.number().int().min(30).max(720).default(180),
  posVendaDiasSeguro: z.number().int().min(30).max(720).default(335),
  /** Ciclo de troca quando o cliente só comprou uma vez */
  posVendaAnosTroca: z.number().min(1).max(10).default(3),
});
export type ConfigInteligencia = z.infer<typeof configInteligenciaSchema>;
export const CONFIG_INTELIGENCIA_PADRAO: ConfigInteligencia = configInteligenciaSchema.parse({});

// ── Resumo diário ──

export interface ResumoDiario {
  data: string;
  texto: string;
  status: 'enviado' | 'sem_canal' | 'sem_telefone' | 'falhou' | 'gerado';
  enviadoEm: string | null;
  vendas: { quantidade: number; faturamentoCentavos: number; lucroCentavos: number };
  alertas: string[];
  recomendacao: string | null;
}

/** Prévia com dados reais para quem não tem o recurso (docs/10: prévias de upgrade). */
export interface PreviaInteligencia {
  carrosComPrecoFora: number;
  capitalTravadoCentavos: number;
  recomendacoes: number;
  posVendaProximos30Dias: number;
}

/** Mediana e quartis (para a faixa de preço do mercado). */
export function quantil(valores: number[], q: number): number | null {
  if (!valores.length) return null;
  const v = [...valores].sort((a, b) => a - b);
  const pos = (v.length - 1) * q;
  const base = Math.floor(pos);
  const resto = pos - base;
  return Math.round(v[base]! + (v[base + 1] !== undefined ? resto * (v[base + 1]! - v[base]!) : 0));
}
